// Helper functions to reduce cognitive complexity

export const loadSellerData = (json, setters) => {
  const be = json.results[0]?.dsaInfo?.businessEntities?.[0] || {};
  const apiAgreementDetails = json.results[0]?.agreementDetails;
  const address = be.address || json.results[0]?.companyAddress || json.results[0]?.address1 || "";

  setters.setProfileData([...json.results]);
  setters.setAccount(json.results[0].userName);
  setters.setCorporateContactName(json.results[0].corporateContactName);
  setters.setCorporateContactEmail(json.results[0].corporateContactEmail);
  setters.setPhoneNumber(json.results[0].phoneNumber);
  setters.setAddress(address);
  setters.setPrivacyPolicy(be.privacyPolicy || "");
  setters.setCompanyRegNumber(be.companyRegNumber || "");
  setters.setCountryOfReg(be.countryOfReg || "");
  setters.setCompanyName(be.entityName || "");
  setters.setCompanyWebsite(be.website || "");
  
  if (apiAgreementDetails) {
    setters.setAgreementDetails(apiAgreementDetails);
    setters.setAgreementStatus(apiAgreementDetails.status === "ACCEPTED");
    setters.setAgreementDate(apiAgreementDetails.statusDate || json.results[0].igniteStoreAgreementDate);
  } else {
    setters.setAgreementDetails(null);
    setters.setAgreementStatus(json.results[0].igniteStoreAgreementStatus);
    setters.setAgreementDate(json.results[0].igniteStoreAgreementDate);
  }
  
  setters.setRegistrationCourt(be.registrationCourt || "");
  setters.setCorporateCompanyState(be.state || "");
  setters.setCorporateCompanyCity(be.city || "");
  setters.setCorporateCompanyZip(be.zip || "");
  setters.setCountryOfCompany(be.country || "");
  setters.setCompanyPhoneNumber(be.phoneNumber || "");
  setters.setCompanyEmail(be.email || "");
};

export const normalizeSellerProfileResults = (results, addressOverride) => {
  if (!Array.isArray(results)) return [];

  return results.map((result) => {
    const existingBusinessEntities = Array.isArray(result?.dsaInfo?.businessEntities)
      ? result.dsaInfo.businessEntities
      : [];
    const firstEntity = existingBusinessEntities[0] || {};
    const normalizedAddress = addressOverride || firstEntity.address || result?.companyAddress || result?.address1 || "";
    const normalizedBusinessEntities = [
      {
        ...firstEntity,
        address: normalizedAddress,
      },
      ...existingBusinessEntities.slice(1),
    ];
    const { address1, ...resultWithoutAddress1 } = result || {};

    return {
      ...resultWithoutAddress1,
      companyAddress: normalizedAddress,
      dsaInfo: {
        ...result?.dsaInfo,
        businessEntities: normalizedBusinessEntities,
      },
    };
  });
};

export const loadOemAdminData = (json, setters) => {
  const be = json.results[0]?.dsaInfo?.businessEntities?.businessEntities[0] || {};
  const address = be.address || json.results[0]?.companyAddress || json.results[0]?.address1 || "";
  
  setters.setAccount(json.results[0].userName);
  setters.setCorporateContactName(json.results[0].corporateContactName);
  setters.setCorporateContactEmail(json.results[0].corporateContactEmail);
  setters.setPhoneNumber(json.results[0].phoneNumber);
  setters.setAddress(address);
  setters.setCompanyRegNumber(be.companyRegNumber || "");
  setters.setCountryOfReg(be.countryOfReg || "");
  setters.setPrivacyPolicy(json.results[0].privacyPolicy);
  setters.setCompanyName(be.entityName || "");
  setters.setCompanyWebsite(json.results[0]?.dsaInfo?.companyWebsite || "");
  setters.setAgreementStatus(json.results[0].igniteStoreAgreementStatus);
  setters.setAgreementDate(json.results[0].igniteStoreAgreementDate);
  setters.setRegistrationCourt(be.registrationCourt || "");
  setters.setCorporateCompanyState(be.state || "");
  setters.setCorporateCompanyCity(be.city || "");
  setters.setCorporateCompanyZip(be.zip || "");
  setters.setCountryOfCompany(be.country || "");
  setters.setCompanyPhoneNumber(be.phone || "");
  setters.setCompanyEmail(json.results[0]?.dsaInfo?.email || "");
};

export const handleApiError = (error, setters) => {
  setters.setComponentName("appError");
  setters.setErrorMessage(error.message);
  setters.setHeaderMessage(error.message === "jwt expired" ? "401 Token Expired" : "Error");
  setters.setOpenErrorPopup(true);
  setters.setLoader(false);
};

export const updateSectionState = (index, readOnlyValue, setters) => {
  if (index === 1) {
    setters.setProfileSection(true);
    setters.setBusinessSection(false);
    if (readOnlyValue) {
      setters.setShowBusinessFlag(false);
      setters.setBusinessReadOnly(true);
    } else {
      setters.setProfileReadOnly(false);
      setters.setBusinessReadOnly(true);
      setters.setShowSaveFlag(true);
      setters.setShowBusinessFlag(false);
    }
  } else if (index === 2) {
    setters.setProfileSection(false);
    setters.setBusinessSection(true);
    if (readOnlyValue) {
      setters.setShowSaveFlag(false);
      setters.setProfileReadOnly(true);
    } else {
      setters.setProfileReadOnly(true);
      setters.setBusinessReadOnly(false);
      setters.setShowSaveFlag(false);
      setters.setShowBusinessFlag(true);
    }
  } else {
    setters.setProfileSection(true);
    setters.setBusinessSection(false);
  }
};

export const resetAllFormErrors = (setters) => {
  setters.setInputCrpNameErrMsg("");
  setters.setInputCrpEmailErrMsg("");
  setters.setInputAddressErrMsg("");
  setters.setInputCompNameErrMsg("");
  setters.setInputCompWebsiteErrMsg("");
  setters.setInputPrivacyLinkErrMsg("");
  setters.setInputRegNumberErrMsg("");
  setters.setInputCountryRegErrMsg("");
  setters.setInputCorporateCompanyZip("");
  setters.setInputRegistrationCourt("");
  setters.setInputCorporateCompanyCity("");
  setters.setInputCorporateCompanyState("");
  setters.setInputCompanyEmailErrMsg("");
  setters.setInputCountryCompanyErrMsg("");
};

export const restoreProfileData = (profileData, currentState, setters) => {
  if (!profileData.length) return;
  
  const be = profileData[0]?.dsaInfo?.businessEntities?.[0] || {};
  const address = be.address || profileData[0]?.companyAddress || profileData[0]?.address1 || "";
  
  setters.setCorporateContactName(currentState.corporateContactName || profileData[0]?.corporateContactName || "");
  setters.setCorporateContactEmail(currentState.corporateContactEmail || profileData[0]?.corporateContactEmail || "");
  setters.setPhoneNumber(currentState.phoneNumber || profileData[0]?.phoneNumber || "");
  setters.setAddress(currentState.address || address);
  setters.setCompanyRegNumber(currentState.companyRegNumber || be.companyRegNumber || "");
  setters.setCountryOfReg(currentState.countryOfReg ||be.countryOfReg || "");
  setters.setCompanyName(currentState.companyName || be.entityName || "");
  setters.setCompanyWebsite(currentState.companyWebsite || be.website || "");
  setters.setPrivacyPolicy(currentState.privacyPolicy || be.privacyPolicy || "");
  setters.setAgreementStatus(profileData[0]?.igniteStoreAgreementStatus || false);
  setters.setAgreementDate(profileData[0]?.igniteStoreAgreementDate || "");
  setters.setRegistrationCourt(currentState.registrationCourt || be.registrationCourt || "");
  setters.setCorporateCompanyState(currentState.corporateCompanyState || be.state || "");
  setters.setCorporateCompanyCity(currentState.corporateCompanyCity || be.city || "");
  setters.setCorporateCompanyZip(currentState.corporateCompanyZip || be.zip || "");
  setters.setCountryOfCompany(currentState.countryOfCompany || be.country || "");
  setters.setCompanyPhoneNumber(currentState.companyPhoneNumber || be.phoneNumber || "");
  setters.setCompanyEmail(currentState.companyEmail || be.email || "");
};

export const getHeaderTitle = (showSaveFlag, showBusinessFlag, isSeller, profileSection) => {
  if ((showSaveFlag || showBusinessFlag) && isSeller) return "Profile Setup";
  return profileSection ? "Profile Info" : "Business Info";
};

export const getFieldLabel = (profileSection, fieldType, isIdpLogin) => {
  const labels = {
    contactName: { profile: "Contact Name", business: "Company Website" },
    contactEmail: { profile: "Contact Email", business: "Registration Court" },
    phoneNumber: { profile: "Phone Number", business: "Registration Number" }
  };
  
  if (profileSection && isIdpLogin) return "";
  return profileSection ? labels[fieldType].profile : labels[fieldType].business;
};

export const getPhoneErrorMessage = (phoneNumber, isValid) => {
  if (!phoneNumber) return "Phone number required";
  return isValid ? undefined : "Invalid phone number";
};

export const renderAgreementStatus = (agreementDetails, agreementDetailsData, agreementStatus, VerifiedIcon, WarningIcon, CampaignIcon) => {
  const currentAgreementDetails = agreementDetails || agreementDetailsData;
  const currentStatus = currentAgreementDetails?.status;
  const hasNewUpdate = currentAgreementDetails?.isNewAgreementAvailable;

  if (currentAgreementDetails) {
    if (currentStatus === 'ACCEPTED' && !hasNewUpdate) {
      return (
        <div className="agreementAcceptedMsg agreementText">
          <VerifiedIcon className="iconCls" />
          <span>&nbsp; Accepted</span>
        </div>
      );
    }
    if (currentStatus === 'NEW_UPDATE_AVAILABLE' || hasNewUpdate) {
      return (
        <div className="agreementNewMsg agreementText">
          <CampaignIcon className="newUpdateIconCls" style={{ fontSize: '16px' }} />
          <span>&nbsp;New Update Available</span>
        </div>
      );
    }
    return (
      <div className="agreementNotAcceptedMsg agreementText">
        <WarningIcon className="iconCls" />
        <span>&nbsp;Not Accepted</span>
      </div>
    );
  }
  
  if (agreementStatus) {
    return (
      <div className="agreementAcceptedMsg agreementText">
        <VerifiedIcon className="iconCls" />
        <span>&nbsp; Accepted</span>
      </div>
    );
  }
  
  return (
    <div className="agreementNotAcceptedMsg agreementText">
      <WarningIcon className="iconCls" />
      <span>&nbsp;Not Accepted</span>
    </div>
  );
};

export const shouldShowAgreementActions = (agreementDetails, agreementDetailsData, agreementStatus) => {
  const currentAgreementDetails = agreementDetails || agreementDetailsData;
  const currentStatus = currentAgreementDetails?.status;
  const hasNewUpdate = currentAgreementDetails?.isNewAgreementAvailable;
  
  return !agreementStatus || 
    currentStatus === 'NOT_ACCEPTED' || 
    currentStatus === 'NEW_UPDATE_AVAILABLE' || 
    currentStatus === 'REJECTED' || 
    hasNewUpdate;
};

export const getAgreementTooltipMessage = (agreement_accepted, disableSubmitAgreement, allBusinessValid, allProfileValid) => {
  if (!agreement_accepted || !disableSubmitAgreement) return "";
  
  if (!allBusinessValid && !allProfileValid) {
    return 'Complete all required fields in "Profile Info" and "Business Info" to enable this action.';
  }
  if (allBusinessValid && !allProfileValid) {
    return 'Complete all required fields in "Profile Info" to enable this action.';
  }
  if (!allBusinessValid && allProfileValid) {
    return 'Complete all required fields in "Business Info" to enable this action.';
  }
  return "";
};

export const acceptAgreement = async (agreementDetails, agreementDetailsData, profileData, { dispatch, fetchAgreementDetails, APIManager, isSeller, Moment }) => {
  const currentAgreementDetails = agreementDetails || agreementDetailsData;
  const acceptDateValue = currentAgreementDetails?.statusDate || new Date().toISOString();
  
  const saveProfileData = {
    ...profileData,
    igniteStoreAgreementStatus: "true",
    igniteStoreAgreementDate: Moment(acceptDateValue).format("YYYY-MM-DD"),
  };

  if (currentAgreementDetails?.agreementId) {
    saveProfileData.igniteStoreAgreementId = currentAgreementDetails.agreementId;
    saveProfileData.igniteStoreAgreementVersion = currentAgreementDetails.version || "";
    saveProfileData.isNewAgreementAvailable = currentAgreementDetails.isNewAgreementAvailable || false;
  }

  const apiCall = isSeller ? APIManager.patchCognitoSelf : APIManager.patchCognitoAdminSelf;
  
  try {
    const json = await apiCall(saveProfileData);
    const updatedAgreementDetails = {
      ...currentAgreementDetails,
      status: 'ACCEPTED',
      statusDate: acceptDateValue,
      isNewAgreementAvailable: false
    };
    
    dispatch(fetchAgreementDetails());
    
    return {
      success: true,
      details: updatedAgreementDetails,
      acceptDate: Moment(acceptDateValue).format("YYYY-MM-DD"),
      results: json?.results
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || "Failed to accept agreement"
    };
  }
};
